import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { type Property } from '@/lib/supabase';
import { MapPin, ArrowUpRight, Square, Euro } from 'lucide-react';

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/verhuur/${property.id}`);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'available':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'option':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'rented':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'available':
        return 'Beschikbaar';
      case 'option':
        return 'Optie';
      case 'rented':
        return 'Verhuurd';
      default:
        return 'Beschikbaar';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'residential':
        return 'Wonen';
      case 'commercial':
        return 'Bedrijf';
      case 'office':
        return 'Kantoor';
      case 'retail':
        return 'Winkel';
      default:
        return type;
    }
  };

  return (
    <Card 
      className="group h-full bg-card border-border/50 shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-2 overflow-hidden cursor-pointer"
      onClick={handleClick}
    >
      <div className="relative overflow-hidden">
        <div className="aspect-[16/10] bg-muted overflow-hidden">
          {property.photos && property.photos.length > 0 ? (
            <img 
              src={property.photos[0].startsWith('http') 
                ? property.photos[0]
                : property.photos[0].startsWith('src/')
                  ? `/${property.photos[0]}`
                  : `https://mgpylqzfxfflpucywzwh.supabase.co/storage/v1/object/public/property-images/${property.photos[0]}`}
              alt={property.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
              <span className="text-muted-foreground font-medium">Foto volgt</span>
            </div>
          )}
        </div>
        
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Status badge */}
        <div className="absolute top-4 left-4">
          <Badge className={`${getStatusColor(property.status)} border font-medium shadow-sm`}>
            {getStatusText(property.status)}
          </Badge>
        </div>
        
        {/* Type badge */}
        <div className="absolute top-4 right-4">
          <Badge variant="secondary" className="bg-white/95 text-primary border font-medium shadow-sm backdrop-blur-sm">
            {getTypeText(property.type)}
          </Badge>
        </div>
        
        {/* Hover icon */}
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-card">
            <ArrowUpRight className="h-5 w-5 text-primary" />
          </div>
        </div>
        
        {/* Hover title overlay */}
        <div className="absolute bottom-4 left-4 right-16 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">
          <span className="text-white/90 text-sm font-medium">Bekijk object →</span>
        </div>
      </div>

      <CardHeader className="pb-3">
        <h3 className="font-serif text-xl font-medium text-primary group-hover:text-primary/80 transition-colors duration-300">
          {property.title}
        </h3>
        <div className="flex items-center text-muted-foreground">
          <MapPin className="h-4 w-4 mr-2 text-accent" />
          <span className="text-sm">{property.address}, {property.city}</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
          {property.summary || property.description || 'Meer informatie beschikbaar op aanvraag.'}
        </p>
        
        {/* Property details badges */}
        <div className="flex flex-wrap gap-2">
          {property.surface_area && (
            <Badge variant="secondary" className="bg-secondary/80 text-foreground/80 text-xs font-medium">
              <Square className="h-3 w-3 mr-1" />
              {property.surface_area} m²
            </Badge>
          )}
          {property.rental_price && (
            <Badge variant="secondary" className="bg-secondary/80 text-foreground/80 text-xs font-medium">
              <Euro className="h-3 w-3 mr-1" />
              {property.rental_price.toLocaleString()} /mnd
            </Badge>
          )}
          {property.amenities && property.amenities.length > 0 && property.amenities.slice(0, 2).map((amenity, index) => (
            <Badge key={index} variant="secondary" className="bg-secondary/80 text-foreground/80 text-xs font-medium">
              {amenity}
            </Badge>
          ))}
          {property.amenities && property.amenities.length > 2 && (
            <Badge variant="secondary" className="text-xs">
              +{property.amenities.length - 2}
            </Badge>
          )}
        </div>
        
        <div className="pt-2">
          <span className="inline-flex items-center text-sm font-medium text-primary group-hover:text-accent transition-colors duration-300">
            Bekijk object details
            <ArrowUpRight className="h-4 w-4 ml-1 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyCard;
